import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Taches.css';

// Enregistrement gardé tel quel : d'autres graphiques de l'appli peuvent en dépendre
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Statuts (valeurs envoyées à l'API inchangées)
const STATUTS = [
  { key: 'ouvert',   label: 'Ouvert',   pluriel: 'Ouvertes',  tone: 'danger', aide: 'À prendre en charge' },
  { key: 'en cours', label: 'En cours', pluriel: 'En cours',  tone: 'warn',   aide: 'Quelqu\'un est dessus' },
  { key: 'soldé',    label: 'Soldé',    pluriel: 'Soldées',   tone: 'ok',     aide: 'Terminées' },
];
const TONE_STATUT = { ouvert: 'danger', 'en cours': 'warn', 'soldé': 'ok' };

// ★ Départements proposés (filtre + suggestions du formulaire)
const DEPARTEMENTS = ['APPRO', 'ADV', 'LOG'];

// Colonnes à présentation spéciale
const COL_LONGUES = ['Description', 'Commentaires'];

/* ── Description : découpe "Clé : valeur" en petits champs (tâches créées par Animation) ── */
const CLES_DESCRIPTION = [
  'Date de livraison prevue', 'Date de livraison prévue', 'Item', 'Commande client', 'Ligne',
  'Commande fournisseur', 'Nom fournisseur', 'Date de réception', 'Date de reception', 'Commentaire',
];
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REGEX_CLES = new RegExp(
  `(${[...CLES_DESCRIPTION].sort((a, b) => b.length - a.length).map(echapper).join('|')})\\s*:`,
  'g'
);

const decouperDescription = (texte) => {
  if (texte == null) return null;
  const s = String(texte);
  const positions = [];
  let m;
  REGEX_CLES.lastIndex = 0;
  while ((m = REGEX_CLES.exec(s)) !== null) {
    positions.push({ label: m[1], debut: m.index, finLabel: REGEX_CLES.lastIndex });
  }
  if (positions.length < 2) return null;
  const champs = [];
  const avant = s.slice(0, positions[0].debut).trim();
  if (avant) champs.push({ label: 'Info', valeur: avant });
  positions.forEach((p, i) => {
    const fin = i + 1 < positions.length ? positions[i + 1].debut : s.length;
    const valeur = s.slice(p.finLabel, fin).trim();
    champs.push({ label: p.label.replace('prevue', 'prévue').replace('reception', 'réception'), valeur: valeur || '—' });
  });
  return champs;
};

/* ── Dates ── */
const pad = (n) => String(n).padStart(2, '0');
const formatDate = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

// "JJ/MM/AAAA" → nombre de jours écoulés
const ageEnJours = (texte) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(texte || '').trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const auj = new Date(); auj.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((auj - d) / 86400000));
};

const libelleAge = (j) => (j === 0 ? "aujourd'hui" : j === 1 ? 'hier' : `il y a ${j} j`);

const normaliser = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const AutoTextarea = ({ value, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} value={value} {...props} />;
};

const Taches = () => {
  const [taches, setTaches]             = useState([]);
  const [columns, setColumns]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [activeStatut, setActiveStatut] = useState('tous');
  const [activeDept, setActiveDept]     = useState('tous');
  const [recherche, setRecherche]       = useState('');
  const [formOuvert, setFormOuvert]     = useState(false);
  const [formData, setFormData]         = useState({ DE: '', Departement: '', Description: '' });
  const [submitting, setSubmitting]     = useState(false);
  const [formMsg, setFormMsg]           = useState('');
  const [formErreur, setFormErreur]     = useState('');

  useEffect(() => {
    document.title = 'MYSSA – Tâches';
  }, []);

  /* ── Chargement (inchangé) ── */
  useEffect(() => {
    const fetchTaches = async () => {
      try {
        const response = await fetch(`${API}/taches`);
        if (!response.ok) throw new Error('Erreur lors du chargement');
        let data = await response.json();
        data = data.map((t) => {
          const { ID, Client_id, Date: rawDate, ...rest } = t;
          const dateObj = new Date(rawDate);
          const formattedDate = isNaN(dateObj) ? '' : formatDate(dateObj);
          const statutKey = Object.keys(rest).find(k => k.toLowerCase() === 'statut');
          return { ...rest, Date: formattedDate, _rawDate: dateObj, Statut: rest[statutKey] || 'ouvert' };
        })
          .sort((a, b) => (a._rawDate || 0) - (b._rawDate || 0))
          .map(({ _rawDate, ...rest }) => rest);
        setTaches(data);
        if (data.length > 0) setColumns(Object.keys(data[0]));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTaches();
  }, []);

  /* ── Compteurs : recalculés à partir des tâches (toujours justes) ── */
  const statusCounts = useMemo(() => {
    const c = { ouvert: 0, 'en cours': 0, 'soldé': 0 };
    taches.forEach(t => { if (c[t.Statut] !== undefined) c[t.Statut]++; });
    return c;
  }, [taches]);

  const total = taches.length;
  const pctSolde = total ? Math.round((statusCounts['soldé'] / total) * 100) : 0;

  /* ── Filtres combinés (statut + département + recherche) ── */
  const filteredTaches = useMemo(() => {
    const q = normaliser(recherche.trim());
    return taches
      .filter(t => activeStatut === 'tous' || t.Statut === activeStatut)
      .filter(t => activeDept === 'tous' || (t['Département'] || t['Departement']) === activeDept)
      .filter(t => !q || Object.values(t).some(v => normaliser(v).includes(q)));
  }, [taches, activeStatut, activeDept, recherche]);

  const countDept = (d) => taches.filter(t => d === 'tous' || (t['Département'] || t['Departement']) === d).length;

  /* ── Formulaire ── */
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const ouvrirModal = () => { setFormMsg(''); setFormErreur(''); setFormOuvert(true); };
  const fermerModal = () => { setFormOuvert(false); setFormErreur(''); };

  // Échap ferme la modale
  useEffect(() => {
    if (!formOuvert) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) { setFormOuvert(false); setFormErreur(''); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [formOuvert, submitting]);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!formData.DE.trim() || !formData.Departement.trim() || !formData.Description.trim()) {
      setFormErreur('Renseigne les 3 champs : De, Département et Description.');
      return;
    }
    setFormErreur('');
    setSubmitting(true);
    setFormMsg('');
    try {
      const response = await fetch(`${API}/taches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Description: formData.Description, Departement: formData.Departement, DE: formData.DE, source: 'tache' }),
      });
      const data = await response.json();
      if (data.success) {
        const newTache = {
          'Numero tache': data.numero_tache,
          De: formData.DE, Département: formData.Departement,
          Description: formData.Description, Statut: 'ouvert',
          Date: formatDate(new Date()),
        };
        setTaches(prev => [...prev, newTache]);
        if (columns.length === 0) setColumns(Object.keys(newTache));
        setFormData({ DE: formData.DE, Departement: '', Description: '' });  // garde "De" pour enchaîner
        setFormMsg(`✅ Tâche ${data.numero_tache ?? ''} créée`);
        setTimeout(() => { setFormOuvert(false); setFormMsg(''); }, 1200);
      } else {
        setFormMsg(`❌ ${data.error || 'Erreur à l\'enregistrement'}`);
      }
    } catch {
      setFormMsg('❌ Erreur serveur');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Changement statut : mis à jour par n° de tâche (le filtre reste juste) ── */
  const handleStatutChange = async (tache, newStatus) => {
    const numero = tache['Numero tache'];
    const ancien = tache.Statut;
    setTaches(prev => prev.map(t => (t['Numero tache'] === numero ? { ...t, Statut: newStatus } : t)));
    try {
      const response = await fetch(`${API}/taches/${numero}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatus }),
      });
      const data = await response.json();
      if (!data.success) {
        setTaches(prev => prev.map(t => (t['Numero tache'] === numero ? { ...t, Statut: ancien } : t)));
        alert('Erreur mise à jour : ' + data.error);
      }
    } catch {
      setTaches(prev => prev.map(t => (t['Numero tache'] === numero ? { ...t, Statut: ancien } : t)));
      alert('Erreur serveur');
    }
  };

  /* ── Rendu d'une cellule ── */
  const cellule = (tache, col) => {
    const val = tache[col];

    if (col === 'Statut') {
      const tone = TONE_STATUT[val] || 'neutral';
      return (
        <td key={col}>
          <select
            className={`tk-select tk-select-${tone}`}
            value={val}
            onChange={(e) => handleStatutChange(tache, e.target.value)}
            aria-label={`Statut de la tâche ${tache['Numero tache'] ?? ''}`}
          >
            {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </td>
      );
    }

    if (col === 'Numero tache') return <td key={col} className="tk-mono tk-strong">{val}</td>;

    if (col === 'Date') {
      const age = ageEnJours(val);
      const vieille = age !== null && age > 7 && tache.Statut !== 'soldé';
      return (
        <td key={col}>
          <div className="tk-mono">{val}</div>
          {age !== null && <div className={`tk-age ${vieille ? 'tk-age-old' : ''}`}>{libelleAge(age)}</div>}
        </td>
      );
    }

    if (col === 'Département' || col === 'Departement') {
      return <td key={col}>{val ? <span className="tk-dept">{val}</span> : <span className="tk-muted">—</span>}</td>;
    }

    if (col === 'Description') {
      const champs = decouperDescription(val);
      if (champs) {
        return (
          <td key={col} className="tk-td-desc">
            <div className="tk-desc-grid">
              {champs.map((c, i) => {
                const vide = c.valeur === '—' || /^-+$/.test(c.valeur);
                return (
                  <div key={i} className={`tk-desc-field ${c.label === 'Commentaire' ? 'tk-desc-comment' : ''} ${vide ? 'tk-desc-empty' : ''}`}>
                    <span className="tk-desc-label">{c.label}</span>
                    <span className="tk-desc-value">{vide ? '—' : c.valeur}</span>
                  </div>
                );
              })}
            </div>
          </td>
        );
      }
      return <td key={col} className="tk-td-long">{val ?? ''}</td>;
    }

    if (col === 'Commentaires') {
      return (
        <td key={col} className="tk-td-long">
          {val ? <div className="tk-comment">{val}</div> : <span className="tk-muted">—</span>}
        </td>
      );
    }

    return <td key={col} className={COL_LONGUES.includes(col) ? 'tk-td-long' : ''}>{val ?? ''}</td>;
  };

  /* ── États de chargement ── */
  if (loading || error) {
    return (
      <div className="tk-page">
        <div className="tk-container">
          <div className={`tk-card tk-empty ${error ? 'tk-empty-error' : ''}`}>
            {error ? `Erreur : ${error}` : 'Chargement des tâches…'}
          </div>
        </div>
      </div>
    );
  }

  const statutActif = STATUTS.find(s => s.key === activeStatut);
  const descLongue  = formData.Description.length;

  return (
    <div className="tk-page">
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Taches.css).
            Exception lisibilité : le bloc multi-champs de la description reste aligné à gauche. */}
      <style>{`
        .tk-table th, .tk-table td { text-align: center !important; vertical-align: middle; }
        .tk-table td.tk-td-desc { text-align: left !important; }
      `}</style>
      <div className="tk-container">

        {/* ── En-tête ── */}
        <div className="tk-header">
          <div className="tk-header-left">
            <div className="tk-title-row">
              <span className="tk-accent" />
              <h1 className="tk-title">Gestion des tâches</h1>
            </div>
           
          </div>
          <button type="button" className="tk-btn tk-btn-primary" onClick={ouvrirModal}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Nouvelle tâche
          </button>
        </div>

        {/* ── Modal : nouvelle tâche ── */}
        {formOuvert && (
          <div className="tk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) fermerModal(); }}>
            <div className="tk-modal" role="dialog" aria-modal="true" aria-labelledby="tk-modal-titre">
              <div className="tk-modal-head">
                <div>
                  <div id="tk-modal-titre" className="tk-modal-title">Nouvelle tâche</div>
                  <div className="tk-modal-sub">Elle sera créée au statut « Ouvert » pour le département choisi.</div>
                </div>
                <button type="button" className="tk-modal-close" aria-label="Fermer" onClick={fermerModal} disabled={submitting}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>

              <div className="tk-modal-body">
                <div className="tk-modal-row">
                  <div className="tk-field">
                    <label className="tk-label" htmlFor="tk-de">De</label>
                    <input
                      id="tk-de"
                      className={`tk-input ${formErreur && !formData.DE.trim() ? 'tk-input-error' : ''}`}
                      name="DE"
                      value={formData.DE}
                      onChange={handleChange}
                      placeholder="Ton nom ou ton service"
                      autoFocus
                    />
                  </div>

                  <div className="tk-field">
                    <label className="tk-label" htmlFor="tk-dept">Pour le département</label>
                    <input
                      id="tk-dept"
                      className={`tk-input ${formErreur && !formData.Departement.trim() ? 'tk-input-error' : ''}`}
                      name="Departement"
                      list="tk-depts"
                      value={formData.Departement}
                      onChange={handleChange}
                      placeholder="ex : APPRO"
                    />
                    <datalist id="tk-depts">{DEPARTEMENTS.map(d => <option key={d} value={d} />)}</datalist>
                    <div className="tk-quick">
                      {DEPARTEMENTS.map(d => (
                        <button
                          type="button"
                          key={d}
                          className={`tk-quick-chip ${formData.Departement === d ? 'active' : ''}`}
                          onClick={() => setFormData({ ...formData, Departement: d })}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="tk-field">
                  <label className="tk-label" htmlFor="tk-desc">
                    <span>Description</span>
                    <span className="tk-label-hint">{descLongue ? `${descLongue} caractères` : ''}</span>
                  </label>
                  <AutoTextarea
                    id="tk-desc"
                    className={`tk-input tk-textarea ${formErreur && !formData.Description.trim() ? 'tk-input-error' : ''}`}
                    name="Description"
                    value={formData.Description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Qu'y a-t-il à faire ? (contexte, référence, commande, échéance…)"
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(); }}
                  />
                </div>

                {formErreur && <div className="tk-msg tk-msg-danger tk-msg-block">{formErreur}</div>}
                {formMsg && (
                  <div className={`tk-msg tk-msg-block tk-msg-${formMsg.startsWith('✅') ? 'ok' : 'danger'}`}>
                    {formMsg.replace(/^(✅|❌)\s*/, '')}
                  </div>
                )}
              </div>

              <div className="tk-modal-foot">
                <span className="tk-modal-hint">Ctrl + Entrée pour créer</span>
                <button type="button" className="tk-btn tk-btn-ghost" onClick={fermerModal} disabled={submitting}>Annuler</button>
                <button type="button" className="tk-btn tk-btn-primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (<><span className="tk-spinner" aria-hidden="true" /> Enregistrement…</>) : 'Créer la tâche'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Compteurs (cliquables = filtre statut) + avancement ── */}
        <div className="tk-kpis">
          {STATUTS.map(({ key, pluriel, tone, aide }) => {
            const actif = activeStatut === key;
            return (
              <button
                key={key}
                type="button"
                className={`tk-kpi tk-kpi-${tone} ${actif ? 'active' : ''}`}
                aria-pressed={actif}
                onClick={() => setActiveStatut(actif ? 'tous' : key)}
              >
                <span className="tk-kpi-top">
                  <span className="tk-kpi-dot" />
                  <span className="tk-kpi-label">{pluriel}</span>
                </span>
                <span className="tk-kpi-value">{statusCounts[key]}</span>
                <span className="tk-kpi-aide">{aide}</span>
              </button>
            );
          })}

          <div className="tk-kpi tk-kpi-progress">
            <span className="tk-kpi-top">
              <span className="tk-kpi-label">Avancement</span>
              <span className="tk-kpi-pct">{pctSolde}%</span>
            </span>
            <div className="tk-bar" role="img" aria-label={`${statusCounts.ouvert} ouvertes, ${statusCounts['en cours']} en cours, ${statusCounts['soldé']} soldées`}>
              {total > 0 && STATUTS.map(s => (
                <span key={s.key} className={`tk-bar-seg tk-bar-${s.tone}`} style={{ width: `${(statusCounts[s.key] / total) * 100}%` }} />
              ))}
            </div>
            <span className="tk-kpi-aide">{statusCounts['soldé']} soldée{statusCounts['soldé'] > 1 ? 's' : ''} sur {total}</span>
          </div>
        </div>

        {/* ── Barre de filtres ── */}
        <div className="tk-toolbar">
          <div className="tk-segment" role="group" aria-label="Filtrer par département">
            {['tous', ...DEPARTEMENTS].map(d => (
              <button
                key={d}
                type="button"
                className={`tk-seg-btn ${activeDept === d ? 'active' : ''}`}
                aria-pressed={activeDept === d}
                onClick={() => setActiveDept(d)}
              >
                {d === 'tous' ? 'Tous' : d}
                <span className="tk-seg-count">{countDept(d)}</span>
              </button>
            ))}
          </div>

          <div className="tk-search">
            <svg className="tk-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="text"
              className={`tk-input tk-input-search ${recherche ? 'active' : ''}`}
              placeholder="Rechercher (n°, item, commande, texte…)"
              aria-label="Rechercher une tâche"
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
            />
            {recherche && <button type="button" className="tk-clear" aria-label="Effacer la recherche" onClick={() => setRecherche('')}>×</button>}
          </div>
        </div>

        {/* ── Tableau ── */}
        <div className="tk-card tk-table-card">
          <div className="tk-table-head">
            <div className="tk-table-head-left">
              <span className="tk-card-title">Tâches</span>
              <span className="tk-count">{filteredTaches.length}{filteredTaches.length !== total && <span className="tk-count-total"> / {total}</span>}</span>
              {statutActif && (
                <button type="button" className={`tk-filter-chip tk-filter-${statutActif.tone}`} onClick={() => setActiveStatut('tous')}>
                  {statutActif.pluriel} <span aria-hidden="true">×</span>
                </button>
              )}
              {activeDept !== 'tous' && (
                <button type="button" className="tk-filter-chip" onClick={() => setActiveDept('tous')}>
                  {activeDept} <span aria-hidden="true">×</span>
                </button>
              )}
            </div>
          </div>

          <div className="tk-table-scroll">
            {filteredTaches.length === 0 ? (
              <div className="tk-empty">
                <div className="tk-empty-title">Aucune tâche trouvée</div>
                <div>Change de filtre, ou crée une nouvelle tâche.</div>
              </div>
            ) : (
              <table className="tk-table">
                <thead>
                  <tr>{columns.map(col => <th key={col} className={COL_LONGUES.includes(col) ? 'tk-th-long' : ''}>{col === 'Numero tache' ? 'N° tâche' : col}</th>)}</tr>
                </thead>
                <tbody>
                  {filteredTaches.map((tache, i) => (
                    <tr key={tache['Numero tache'] ?? i} className={`tk-row-${TONE_STATUT[tache.Statut] || 'neutral'}`}>
                      {columns.map(col => cellule(tache, col))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Taches;